import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';

const publicAgents = [
  { label: 'Governance Audit', path: '/agents/governance-audit', desc: 'Automate policy-as-code compliance and digital transformation audit trails.' },
  { label: 'Doc Parsing Agent', path: '/agents/doc-parsing', desc: 'Extract data from public records, applications, and regulatory filings.' },
  { label: 'Interview Coach', path: '/agents/interview-agent', desc: 'Structured interview generation and candidate scoring for public sector recruitment.' },
  { label: 'Cloud Architect Agent', path: '/agents/cloud-architect', desc: 'Sovereign cloud architecture design and review for government infrastructure.' },
  { label: 'SQL Agent', path: '/agents/sql-agent', desc: 'Query public data sets and operational metrics using secure, governed natural language.' },
];

const faqs = [
  {
    q: 'How does Eagentix support sovereign data requirements?',
    a: 'Eagentix supports fully air-gapped and on-premises deployment, ensuring all data and model processing stays within secure, government-controlled infrastructure.',
  },
  {
    q: 'Are the AI agents compliant with government standards?',
    a: 'Yes. Eagentix agents are designed for strict governance, featuring full audit trails, human-in-the-loop approvals, and policy-as-code guardrails.',
  },
  {
    q: 'How can AI agents accelerate digital transformation in the public sector?',
    a: 'By automating document processing, citizen inquiry routing, and internal compliance audits, agents reduce operational friction and accelerate service delivery.',
  },
];

export default function PublicSectorAI() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="AI for Public Sector & Government: Enterprise Agents | Eagentix"
        description="Eagentix provides sovereign enterprise AI agents for the public sector — automating compliance, document processing, and digital transformation."
        pathname="/industries/public-sector-ai-agents"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Industry · Public Sector
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              Enterprise AI Agents for the Public Sector
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              Enable secure, sovereign, and governed AI transformation. Eagentix provides purpose-built agents designed for the unique requirements of government-adjacent operations.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://app.eagentix.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
              >
                Get Started
              </a>
              <a
                href="/#contact"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Public Sector Solutions */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            AI Solutions for Governed Operations
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Eagentix agents help public sector organisations automate with transparency and security.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            {publicAgents.map((agent, i) => (
              <Link
                key={i}
                to={agent.path}
                style={{ ...card, textDecoration: 'none', display: 'block' }}
              >
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#0071e3', marginBottom: '8px' }}>{agent.label}</h3>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.65, margin: 0 }}>{agent.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sovereign AI */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Sovereign Deployment Models
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Absolute control over data, models, and execution environment.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {[
              {
                title: 'Air-Gapped Ready',
                desc: 'Deploy agents in fully disconnected environments for the most sensitive public sector data requirements.',
              },
              {
                title: 'Policy-as-Code',
                desc: 'Define operational boundaries and ethical guardrails that are strictly enforced by the agent harness.',
              },
              {
                title: 'Transparent Audit',
                desc: 'Every action is recorded in immutable logs, providing complete transparency for regulatory and public oversight.',
              },
            ].map((item, i) => (
              <div key={i} style={card}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{item.title}</h3>
                <p style={{ color: '#475569', lineHeight: 1.75, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px' }}>
            Public Sector AI FAQ
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#0071e3', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 12px 0' }}>{faq.q}</h3>
                <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.75, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Ready for Governed AI Transformation?
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Book a strategy session today to explore secure and sovereign AI agent deployment for the public sector.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.eagentix.com/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Start Free
            </a>
            <a
              href="/#contact"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              Book a Meeting
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
